import { Box, Typography, Icon } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import {
  Translator,
  formatMoney,
  ReplaceAllInstances,
  enumsCashback,
} from '@itarget/shared'

const useStyles = makeStyles((theme) => ({
  gridStyle: {
    display: 'flex',
    alignItems: 'flex-end',
    JustifyContent: 'center',
    padding: theme.spacing(1),
  },
  sessionStyle: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 1),
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.sessionCashback.backgroundColor,
  },
  sectionIcon: {
    width: theme.typography.pxToRem(36),
    height: theme.typography.pxToRem(36),
    borderRadius: '100%',
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.sessionCashback.iconBackgroundColor,
    '&.material-icons': {
      fontSize: theme.typography.pxToRem(28),
      color: theme.palette.sessionCashback.iconColor,
    },
  },
  accumulationStyle: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexDirection: 'column',
    '& $text': {
      color: theme.palette.sessionCashback.accumulation.color,
    },
    '& $prominenceBox': {
      backgroundColor:
        theme.palette.sessionCashback.accumulation.prominenceBackgroundColor,
    },
    '& $secondLine': {
      color: theme.palette.sessionCashback.accumulation.amountColor,
    },
    '& $thirtyLine': {
      color: theme.palette.sessionCashback.accumulation.prominenceColor,
    },
  },
  rescueStyle: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexDirection: 'column',
    '& $text': {
      color: theme.palette.sessionCashback.rescue.color,
    },
    '& $prominenceBox': {
      backgroundColor:
        theme.palette.sessionCashback.rescue.prominenceBackgroundColor,
    },
    '& $secondLine': {
      color: theme.palette.sessionCashback.rescue.amountColor,
    },
    '& $thirtyLine': {
      color: theme.palette.sessionCashback.rescue.prominenceColor,
    },
  },
  verticalBox: {
    flex: 1,
    display: 'flex',
    gap: theme.spacing(0.5),
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(0, 1),
  },
  text: {
    lineHeight: 1,
    fontSize: theme.typography.pxToRem(14),
  },
  firstLine: {},
  secondLine: {
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(16),
  },
  thirtyLine: {
    fontSize: theme.typography.pxToRem(12),
  },
  prominenceBox: {
    padding: theme.spacing(0.5, 1),
  },
}))

const SessionCashback = (props) => {
  const {
    factor = 0,
    factorType,
    payWithCashback,
    maxPercentageToPayWithCashback = 0,
  } = props
  const { PRODUCTS_CAMPINGS } = enumsCashback
  const { toReal } = formatMoney
  const styles = useStyles()
  let fixe = 0
  const hasFixe = !!factor && factorType === PRODUCTS_CAMPINGS.FACTOR_TYPE.FIXE
  if (hasFixe) {
    fixe = Number(factor).toFixed(2)
  }

  let percentage = 0
  const hasPercentage =
    !!factor && factorType === PRODUCTS_CAMPINGS.FACTOR_TYPE.MULTIPLIER
  if (hasPercentage) {
    percentage = (Number(factor) * 100).toFixed(0)
  }

  const canPayWithCashback =
    payWithCashback === PRODUCTS_CAMPINGS.PAY_WITH_CASHBACK.ALLOWED
  const fixedMaxPercentageToPayWithCashback = Number(
    maxPercentageToPayWithCashback,
  ).toFixed(0)

  const sessionCashback = {
    accumulation: {
      firstLine: Translator({ transRef: 'Acumule' }).props.children,
      thirtyLine: Translator({ transRef: 'em cashback' }).props.children,
      className: styles.accumulationStyle,
      conditions: {
        fixe: {
          value: toReal(fixe),
          rule: hasFixe,
          translation: Translator({ transRef: 'R$ %s' }).props.children,
        },
        percentage: {
          value: percentage,
          rule: hasPercentage,
          translation: Translator({ transRef: '%s%' }).props.children,
        },
      },
    },
    rescue: {
      firstLine: Translator({ transRef: 'Resgate até' }).props.children,
      thirtyLine: Translator({ transRef: 'com seu cashback' }).props.children,
      className: styles.rescueStyle,
      conditions: {
        percentage: {
          value: fixedMaxPercentageToPayWithCashback,
          rule: canPayWithCashback,
          className: styles.ChipDefaultStyle,
          translation: Translator({ transRef: '%s% off' }).props.children,
        },
      },
    },
  }

  const hasSessionCashbackAccumulationToShow = Object.values(
    sessionCashback.accumulation.conditions,
  ).some((value) => value.rule === true)
  const hasSessionCashbackRescueToShow = Object.values(
    sessionCashback.rescue.conditions,
  ).some((value) => value.rule === true)

  if (!hasSessionCashbackAccumulationToShow || !hasSessionCashbackRescueToShow)
    return null

  return (
    <Box
      flex={1}
      padding={1}
      display="flex"
      flexWrap="wrap"
      alignItems="flex-end"
      className={styles.gridStyle}
      container
    >
      <Box className={styles.sessionStyle}>
        <Icon className={styles.sectionIcon}>attach_money</Icon>

        {hasSessionCashbackAccumulationToShow && (
          <Box className={sessionCashback.accumulation.className}>
            <Box className={styles.verticalBox}>
              <Typography className={`${styles.text} ${styles.firstLine}`}>
                {sessionCashback.accumulation.firstLine}
              </Typography>
              {Object.entries(sessionCashback.accumulation.conditions).map(
                ([key, { rule, value, translation }]) => {
                  if (!rule) return null
                  return (
                    <Typography
                      key={`accumulation_${key}`}
                      className={`${styles.text} ${styles.secondLine}`}
                    >
                      {ReplaceAllInstances(translation, [value])}
                    </Typography>
                  )
                },
              )}
            </Box>
            <Box className={styles.prominenceBox}>
              <Typography className={`${styles.text} ${styles.thirtyLine}`}>
                {sessionCashback.accumulation.thirtyLine}
              </Typography>
            </Box>
          </Box>
        )}

        {hasSessionCashbackRescueToShow && (
          <Box className={sessionCashback.rescue.className}>
            <Box className={styles.verticalBox}>
              <Typography className={`${styles.text} ${styles.firstLine}`}>
                {sessionCashback.rescue.firstLine}
              </Typography>
              {Object.entries(sessionCashback.rescue.conditions).map(
                ([key, { rule, value, translation }]) => {
                  if (!rule) return null
                  return (
                    <Typography
                      key={`rescue_${key}`}
                      className={`${styles.text} ${styles.secondLine}`}
                    >
                      {ReplaceAllInstances(translation, [value])}
                    </Typography>
                  )
                },
              )}
            </Box>
            <Box className={styles.prominenceBox}>
              <Typography className={`${styles.text} ${styles.thirtyLine}`}>
                {sessionCashback.rescue.thirtyLine}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default SessionCashback
